import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import { getEmptyCompany } from '../../interfaces/Company/Company';
import {
  createCompany,
  deleteCompany,
  getCompany,
  updateCompany,
} from '../../api/Company';
import { sharedStyles } from '../../utils/Style';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Waiting } from '../../components/Waiting/Waiting';

export const ModifyCompanyPage = () => {
  const navigate = useNavigate();

  const { companyID: companyIDAsStr } = useParams<string>();

  const companyID = companyIDAsStr ? Number(companyIDAsStr) : undefined;
  const isEditing = Number.isFinite(companyID);

  const [company, setCompany] = useState(getEmptyCompany());
  const [loading, setLoading] = useState(false);
  const [clickedDelete, setClickedDelete] = useState(false);

  const refreshCompany = () => {
    setLoading(true);
    getCompany(companyID!)
      .then((c) => setCompany(c))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isEditing) {
      refreshCompany();
    }
  }, [companyIDAsStr]);

  const handleSave = () => {
    if (isEditing) {
      setLoading(true);
      updateCompany(company)
        .then((updatedCompany) => {
          toast.success('Bayi başarıyla güncellendi');
          setCompany(updatedCompany);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    } else {
      setLoading(true);
      createCompany(company)
        .then((createdCompany) => {
          toast.success('Bayi başarıyla oluşturuldu');
          navigate(`/users/companies/${createdCompany.id}`);
        })
        .catch((err) => toast.error(err.message))
        .finally(() => setLoading(false));
    }
  };

  const handleDelete = () => {
    if (!isEditing) {
      return;
    }

    setLoading(true);
    deleteCompany(company.id)
      .then(() => {
        toast.success('Bayi başarıyla silindi');
        goBack();
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const goBack = () => navigate('/users/companies');

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      {clickedDelete && (
        <ConfirmDialog
          content='Gerçekten seçili bayiyi silmek istiyor musunuz'
          open={clickedDelete}
          onClose={() => setClickedDelete(false)}
          onConfirm={handleDelete}
          color='error'
        />
      )}
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          mb: 3,
          gap: 1.5,
        }}
      >
        <IconButton onClick={goBack} sx={{ ...sharedStyles.backIcon }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant='h5'>
          {isEditing ? 'Bayiyi Düzenle' : 'Yeni Bayi'}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {isEditing && (
          <Button
            variant='contained'
            color='error'
            size='small'
            startIcon={<DeleteIcon />}
            onClick={() => setClickedDelete(true)}
            sx={{ ...sharedStyles.buttonText }}
          >
            Sil
          </Button>
        )}
      </Box>
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 400,
          height: 'fit-content',
          p: 3,
          gap: 1.5,
        }}
      >
        <TextField
          label='İsim'
          variant='outlined'
          size='small'
          value={company.title}
          onChange={(e) => setCompany({ ...company, title: e.target.value })}
          fullWidth
        />
        <FormControl fullWidth>
          <InputLabel>Fiyat Türü</InputLabel>
          <Select
            label='Fiyat Türü'
            size='small'
            value={company.priceGroup}
            onChange={(e) =>
              setCompany({ ...company, priceGroup: Number(e.target.value) })
            }
          >
            {[1, 2, 3, 4, 5].map((group) => (
              <MenuItem key={group} value={group}>
                {group}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={company.canUsePointSystem}
              onChange={() =>
                setCompany({
                  ...company,
                  canUsePointSystem: !company.canUsePointSystem,
                })
              }
            />
          }
          label={
            <Typography sx={sharedStyles.subtitle2}>
              Puan Sistemi Kullanılabilirliği
            </Typography>
          }
        />
        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText }}
        >
          Kaydet
        </Button>
      </Paper>
    </Box>
  );
};
